import type { ProjectSummary } from "@calcplan/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArrowUpRight,
  Building2,
  ChevronDown,
  Clock3,
  FolderOpen,
  FileText,
  Grid2X2,
  Import,
  LayoutList,
  MoreHorizontal,
  Plus,
  Search,
  Sparkles,
  SquareDashed,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { preloadEditorRoute } from "../editor/editor-route-loader";

interface Status {
  id: string;
  code: string;
  name: string;
  color: string;
}

export function DashboardPage() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [archived, setArchived] = useState(false);
  const [trash, setTrash] = useState(false);
  const [view, setView] = useState<"grid" | "list">(
    () => (localStorage.getItem("calcplan-project-view") as "grid" | "list") || "grid",
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [menuProject, setMenuProject] = useState<ProjectSummary | null>(null);

  const statusesQuery = useQuery({
    queryKey: ["statuses"],
    queryFn: () => api<{ data: Status[] }>("/project-statuses"),
  });
  const projectsQuery = useQuery({
    queryKey: ["projects", search, status, archived, trash],
    queryFn: () => {
      const params = new URLSearchParams({
        query: search,
        status,
        archived: String(archived),
        trash: String(trash),
      });
      return api<{ data: ProjectSummary[] }>(`/projects?${params}`);
    },
  });
  const usageQuery = useQuery({
    queryKey: ["usage"],
    queryFn: () => api<{ data: { cycle: { used: number } } }>("/plan-usage"),
  });

  const projects = projectsQuery.data?.data ?? [];
  const recent = useMemo(() => projects.slice(0, 3), [projects]);
  const activeStatus = statusesQuery.data?.data.find((item) => item.id === status);

  useEffect(() => {
    if (!projects.length) return;
    const startPreload = () => preloadEditorRoute();
    const idleWindow: {
      requestIdleCallback?: Window["requestIdleCallback"];
      cancelIdleCallback?: Window["cancelIdleCallback"];
    } = window;
    if (idleWindow.requestIdleCallback && idleWindow.cancelIdleCallback) {
      const idleId = idleWindow.requestIdleCallback(startPreload, { timeout: 1_500 });
      return () => idleWindow.cancelIdleCallback?.(idleId);
    }
    const timeoutId = window.setTimeout(startPreload, 300);
    return () => window.clearTimeout(timeoutId);
  }, [projects.length]);

  function changeView(next: "grid" | "list") {
    setView(next);
    localStorage.setItem("calcplan-project-view", next);
  }

  const archiveMutation = useMutation({
    mutationFn: (project: ProjectSummary) =>
      api(`/projects/${project.id}/${trash ? "restore-from-trash" : archived ? "restore" : "archive"}`, { method: "POST" }),
    onSuccess: () => {
      setMenuProject(null);
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (project: ProjectSummary) =>
      api(`/projects/${project.id}${trash ? "/permanent" : ""}`, { method: "DELETE" }),
    onSuccess: () => {
      setMenuProject(null);
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  return (
    <div className="page dashboard-page">
      <header className="page-header">
        <div>
          <span className="page-kicker">Workspace · {session?.tenant.name}</span>
          <h1>Bom trabalho, {session?.user.displayName.split(" ")[0]}.</h1>
          <p>Continue de onde parou ou dê forma a uma nova ideia.</p>
        </div>
        <button className="button button--primary button--large" onClick={() => setCreateOpen(true)}>
          <Plus size={18} /> Novo projeto
        </button>
      </header>

      <section className="dashboard-overview" aria-label="Resumo">
        <div className="overview-main">
          <div className="overview-main__copy">
            <span className="eyebrow eyebrow--dark">Em andamento</span>
            <h2>{recent[0]?.name ?? "Seu próximo ambiente"}</h2>
            <p>
              {recent[0]
                ? `Revisão ${recent[0].currentRevision} · ${recent[0].areaM2.toLocaleString("pt-BR")} m²`
                : "Crie uma planta em branco ou comece por uma sala de 10 × 10 m."}
            </p>
            <button
              className="button button--light"
              onClick={() => recent[0] ? navigate(`/editor?id=${recent[0].id}`) : setCreateOpen(true)}
              onPointerEnter={recent[0] ? preloadEditorRoute : undefined}
              onFocus={recent[0] ? preloadEditorRoute : undefined}
            >
              {recent[0] ? "Continuar projeto" : "Criar projeto"} <ArrowUpRight size={17} />
            </button>
          </div>
          <div className="overview-main__plan" aria-hidden="true">
            <div className="mini-plan mini-plan--large">
              <span className="mini-plan__room mini-plan__room--a" />
              <span className="mini-plan__room mini-plan__room--b" />
              <span className="mini-plan__room mini-plan__room--c" />
              <span className="mini-plan__door" />
              <i>3D</i>
            </div>
          </div>
        </div>
        <div className="overview-side">
          <span className="overview-side__icon"><Building2 size={19} /></span>
          <span>Projetos ativos</span>
          <strong>{projects.filter((project) => !project.archivedAt).length}</strong>
          <small>neste workspace</small>
        </div>
        <div className="overview-side">
          <span className="overview-side__icon overview-side__icon--cyan"><Sparkles size={19} /></span>
          <span>Projetos criados</span>
          <strong>{usageQuery.data?.data.cycle.used ?? "—"}</strong>
          <small>desde o início, sem limite</small>
        </div>
      </section>

      <section className="projects-section">
        <div className="projects-section__title">
          <div>
            <h2>Seus projetos</h2>
            <span>{projects.length} {projects.length === 1 ? "projeto" : "projetos"}</span>
          </div>
          <div className="view-toggle" aria-label="Modo de visualização">
            <button className={view === "grid" ? "active" : ""} onClick={() => changeView("grid")} aria-label="Ver em grade">
              <Grid2X2 size={17} />
            </button>
            <button className={view === "list" ? "active" : ""} onClick={() => changeView("list")} aria-label="Ver em lista">
              <LayoutList size={18} />
            </button>
          </div>
        </div>

        <div className="project-toolbar">
          <label className="search-field">
            <Search size={18} />
            <input
              type="search"
              placeholder="Buscar projeto por nome…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && <button onClick={() => setSearch("")} aria-label="Limpar busca"><X size={16} /></button>}
          </label>
          <label className="select-field">
            <span className="status-dot" style={{ background: activeStatus?.color ?? "#64748B" }} />
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="all">Todos os status</option>
              {statusesQuery.data?.data.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            <ChevronDown size={16} />
          </label>
          <label className="switch-label">
            <input type="checkbox" checked={archived} disabled={trash} onChange={(event) => { setArchived(event.target.checked); if (event.target.checked) setTrash(false); }} />
            <span />
            Arquivados
          </label>
          <label className="switch-label">
            <input type="checkbox" checked={trash} onChange={(event) => { setTrash(event.target.checked); if (event.target.checked) setArchived(false); }} />
            <span />
            Lixeira
          </label>
        </div>

        {projectsQuery.isLoading ? (
          <ProjectSkeletons />
        ) : projectsQuery.isError ? (
          <div className="empty-state">
            <FolderOpen size={31} />
            <h3>Não foi possível carregar seus projetos</h3>
            <p>{projectsQuery.error instanceof ApiError ? projectsQuery.error.message : "Tente novamente."}</p>
            <button className="button button--secondary" onClick={() => void projectsQuery.refetch()}>Tentar novamente</button>
          </div>
        ) : projects.length === 0 ? (
          <div className="empty-state">
            <SquareDashed size={34} />
            <h3>{search || status !== "all" ? "Nenhum projeto encontrado" : trash ? "A lixeira está vazia" : archived ? "A lista de arquivados está vazia" : "Seu primeiro projeto começa aqui"}</h3>
            <p>{search || status !== "all" ? "Ajuste os filtros ou limpe a busca." : "Crie uma planta em branco ou use uma base pronta."}</p>
            {search || status !== "all" ? (
              <button className="button button--secondary" onClick={() => { setSearch(""); setStatus("all"); }}>Limpar filtros</button>
            ) : !archived ? (
              <button className="button button--primary" onClick={() => setCreateOpen(true)}><Plus size={17} /> Novo projeto</button>
            ) : null}
          </div>
        ) : (
          <div className={`project-collection project-collection--${view}`}>
            {projects.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                index={index}
                view={view}
                onOpen={() => navigate(`/editor?id=${project.id}`)}
                onPreload={preloadEditorRoute}
                onMenu={() => setMenuProject(project)}
              />
            ))}
          </div>
        )}
      </section>

      {createOpen && <CreateProjectDialog onClose={() => setCreateOpen(false)} />}
      {menuProject && (
        <ProjectActionDialog
          project={menuProject}
          archived={archived}
          trash={trash}
          loading={archiveMutation.isPending || deleteMutation.isPending}
          onClose={() => setMenuProject(null)}
          onArchive={() => archiveMutation.mutate(menuProject)}
          onOpen={() => navigate(`/editor?id=${menuProject.id}`)}
          onDetails={() => navigate(`/projects/${menuProject.id}`)}
          onDelete={() => deleteMutation.mutate(menuProject)}
        />
      )}
    </div>
  );
}

function ProjectCard({
  project,
  index,
  view,
  onOpen,
  onPreload,
  onMenu,
}: {
  project: ProjectSummary;
  index: number;
  view: "grid" | "list";
  onOpen(): void;
  onPreload(): void;
  onMenu(): void;
}) {
  const updated = new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" });
  const days = Math.round((new Date(project.updatedAt).getTime() - Date.now()) / 86_400_000);
  return (
    <article className="project-card">
      <button
        className="project-card__open"
        onClick={onOpen}
        onPointerEnter={onPreload}
        onFocus={onPreload}
        aria-label={`Abrir ${project.name}`}
      >
        <div className={`project-thumbnail project-thumbnail--${(index % 4) + 1}`}>
          {project.thumbnail ? (
            <img className="project-thumbnail__plan" src={project.thumbnail} alt="" />
          ) : (
            <div className="mini-plan">
              <span className="mini-plan__room mini-plan__room--a" />
              <span className="mini-plan__room mini-plan__room--b" />
              <span className="mini-plan__room mini-plan__room--c" />
              <span className="mini-plan__door" />
            </div>
          )}
          <span className="project-card__revision">R{project.currentRevision}</span>
        </div>
        <div className="project-card__body">
          <div>
            <h3>{project.name}</h3>
            <span className="status-badge" style={{ "--status-color": project.statusColor } as React.CSSProperties}>
              <i /> {project.statusName}
            </span>
          </div>
          <p>{project.roomCount || "—"} ambiente{project.roomCount === 1 ? "" : "s"} · {project.areaM2 || "—"} m²</p>
          <span className="project-card__time"><Clock3 size={14} /> Atualizado {updated.format(days, "day")}</span>
        </div>
      </button>
      <button className="project-card__menu" onClick={onMenu} aria-label={`Ações para ${project.name}`}>
        <MoreHorizontal size={20} />
      </button>
    </article>
  );
}

function ProjectSkeletons() {
  return (
    <div className="project-collection project-collection--grid" aria-busy="true">
      {[1, 2, 3].map((item) => <div key={item} className="project-skeleton"><span /><i /><i /></div>)}
    </div>
  );
}

function CreateProjectDialog({ onClose }: { onClose(): void }) {
  const [name, setName] = useState("Nova planta");
  const [template, setTemplate] = useState<"blank" | "square-10m">("blank");
  const dialog = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () =>
      api<{ data: ProjectSummary }>("/projects", {
        method: "POST",
        body: {
          template,
          name,
          idempotencyKey: crypto.randomUUID(),
        },
      }),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      navigate(`/editor?id=${response.data.id}`);
    },
  });

  useEffect(() => {
    dialog.current?.focus();
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !mutation.isPending) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [mutation.isPending, onClose]);

  return (
    <div className="dialog-backdrop" role="presentation">
      <div className="dialog create-dialog" role="dialog" aria-modal="true" aria-labelledby="create-title" tabIndex={-1} ref={dialog}>
        <header>
          <div><span className="eyebrow">Novo projeto</span><h2 id="create-title">Escolha um ponto de partida</h2></div>
          <button className="icon-button" onClick={onClose} disabled={mutation.isPending} aria-label="Fechar"><X size={20} /></button>
        </header>
        <div className="template-grid">
          <button className={template === "blank" ? "template-card active" : "template-card"} onClick={() => setTemplate("blank")}>
            <span className="template-card__visual template-card__visual--blank"><Plus size={24} /></span>
            <strong>Planta em branco</strong>
            <small>Comece desenhando as paredes</small>
          </button>
          <button className={template === "square-10m" ? "template-card active" : "template-card"} onClick={() => setTemplate("square-10m")}>
            <span className="template-card__visual"><span className="template-square" /></span>
            <strong>Quadrada 10 × 10</strong>
            <small>Sala pronta com piso e porta</small>
          </button>
          <button className="template-card" disabled title="Adapter indisponível neste ambiente">
            <span className="template-card__visual"><Import size={24} /></span>
            <strong>Importar Promob</strong>
            <small>Requer adapter licenciado</small>
          </button>
          <button className="template-card" disabled>
            <span className="template-card__visual"><Sparkles size={24} /></span>
            <strong>Planta com IA</strong>
            <small>Preview sujeito à revisão</small>
          </button>
        </div>
        <label className="field">
          Nome do projeto
          <input value={name} onChange={(event) => setName(event.target.value)} maxLength={120} autoFocus />
        </label>
        {mutation.isError && <div className="form-error" role="alert">{mutation.error instanceof ApiError ? mutation.error.message : "Não foi possível criar o projeto."}</div>}
        <footer>
          <button className="button button--secondary" onClick={onClose} disabled={mutation.isPending}>Cancelar</button>
          <button className="button button--primary" onClick={() => mutation.mutate()} disabled={!name.trim() || mutation.isPending}>
            {mutation.isPending ? "Criando…" : "Criar projeto"} <ArrowUpRight size={17} />
          </button>
        </footer>
      </div>
    </div>
  );
}

function ProjectActionDialog({
  project,
  archived,
  trash,
  loading,
  onClose,
  onArchive,
  onOpen,
  onDetails,
  onDelete,
}: {
  project: ProjectSummary;
  archived: boolean;
  trash: boolean;
  loading: boolean;
  onClose(): void;
  onArchive(): void;
  onOpen(): void;
  onDetails(): void;
  onDelete(): void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  return (
    <div className="dialog-backdrop">
      <div className="dialog action-dialog" role="dialog" aria-modal="true" aria-labelledby="action-title">
        <header><div><span className="eyebrow">Ações do projeto</span><h2 id="action-title">{project.name}</h2></div><button className="icon-button" onClick={onClose} aria-label="Fechar"><X size={20} /></button></header>
        {!trash && <button className="dialog-action" onClick={onOpen}><FolderOpen size={19} /><span><strong>Abrir projeto</strong><small>Continuar na revisão {project.currentRevision}</small></span></button>}
        {!trash && <button className="dialog-action" onClick={onDetails}><FileText size={19} /><span><strong>Informações e documentos</strong><small>Clientes, anexos, anotações, render e histórico</small></span></button>}
        <button className="dialog-action" onClick={onArchive} disabled={loading}><Archive size={19} /><span><strong>{trash ? "Restaurar da lixeira" : archived ? "Restaurar" : "Arquivar"}</strong><small>{trash || archived ? "Voltar à coleção ativa" : "Mover para projetos arquivados"}</small></span></button>
        <button className="dialog-action dialog-action--danger" onClick={() => confirmDelete ? onDelete() : setConfirmDelete(true)} disabled={loading}><Trash2 size={19} /><span><strong>{confirmDelete ? (trash ? "Confirmar exclusão permanente" : "Confirmar envio à lixeira") : trash ? "Excluir permanentemente" : "Mover para lixeira"}</strong><small>{trash ? "Esta ação não pode ser desfeita" : "O projeto poderá ser restaurado depois"}</small></span></button>
      </div>
    </div>
  );
}
